import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/feature/MangaCard';
import { mangas } from '@/mocks/mangas';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function buildChapters(total: number) {
  const chapters: { no: number; title: string; date: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= total; i++) {
    const d = new Date(now.getTime() - (total - i) * 7 * 24 * 60 * 60 * 1000);
    const month = d.getMonth() + 1;
    chapters.push({ no: i, title: `第${i}话`, date: `${month}月${d.getDate()}日` });
  }
  return chapters.reverse();
}

export default function MangaDetailPage() {
  const { id = '' } = useParams();
  const manga = mangas.find((m) => m.id === id);
  const [fav, setFav] = useState(false);

  useDocumentMeta(
    manga ? `${manga.title} - 樱花漫画网在线观看` : '漫画详情 - 樱花漫画网',
    manga
      ? `${manga.title}是樱花漫画网${manga.categoryName}作品，${manga.description} 在线免费阅读，收藏追更更方便。`
      : undefined,
  );

  const chapters = useMemo(() => (manga ? buildChapters(manga.chapters) : []), [manga]);

  if (!manga) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-20 text-center">
          <p className="font-heading text-2xl font-bold text-foreground-900">未找到这部漫画</p>
          <p className="mt-2 text-sm text-foreground-500">它可能已被移除，或链接有误。</p>
          <Link to="/category/all" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
            返回漫画列表
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const related = mangas.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 5);
  const latestChapters = chapters.slice(0, 12);

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        {/* 详情头部 */}
        <section className="bg-background-100 py-8 md:py-12">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="text-xs text-foreground-500">
              <ol className="flex items-center gap-1.5">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li>/</li>
                <li><Link to={`/category/${manga.category}`} className="hover:text-primary-600">{manga.categoryName}</Link></li>
                <li>/</li>
                <li aria-current="page" className="text-foreground-800">{manga.title}</li>
              </ol>
            </nav>

            <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
              {/* 封面 */}
              <div className="shrink-0">
                <img
                  src={manga.cover}
                  alt={`${manga.title} 封面`}
                  title={`${manga.title} - ${manga.categoryName}`}
                  className="w-48 rounded-xl object-cover object-top shadow-lg md:w-60"
                />
              </div>

              {/* 信息 */}
              <div className="flex-1">
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">{manga.title}</h1>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-medium text-background-50 ${manga.status === '连载中' ? 'bg-primary-500' : 'bg-secondary-500'}`}>
                    {manga.status}
                  </span>
                  {manga.tags.map((t) => (
                    <span key={t} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">{t}</span>
                  ))}
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 md:grid-cols-4">
                  <div className="rounded-lg bg-background-50 p-3">
                    <dt className="text-xs text-foreground-500">作者</dt>
                    <dd className="mt-1 font-medium text-foreground-900">{manga.author}</dd>
                  </div>
                  <div className="rounded-lg bg-background-50 p-3">
                    <dt className="text-xs text-foreground-500">更新至</dt>
                    <dd className="mt-1 font-medium text-foreground-900">{manga.latestChapter}</dd>
                  </div>
                  <div className="rounded-lg bg-background-50 p-3">
                    <dt className="text-xs text-foreground-500">评分</dt>
                    <dd className="mt-1 flex items-center gap-1 font-medium text-accent-600">
                      <i className="ri-star-fill"></i>{manga.rating}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-background-50 p-3">
                    <dt className="text-xs text-foreground-500">热度</dt>
                    <dd className="mt-1 font-medium text-foreground-900">{manga.views}</dd>
                  </div>
                </dl>

                <p className="mt-5 max-w-3xl text-sm leading-7 text-foreground-600">{manga.description}</p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="#chapters"
                    className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 whitespace-nowrap transition hover:bg-primary-600"
                  >
                    <i className="ri-play-fill"></i>
                    开始阅读
                  </Link>
                  <button
                    type="button"
                    onClick={() => setFav((v) => !v)}
                    className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold whitespace-nowrap transition ${
                      fav ? 'bg-accent-500 text-background-50 hover:bg-accent-600' : 'bg-background-200 text-foreground-700 hover:bg-accent-100'
                    }`}
                  >
                    <i className={`${fav ? 'ri-bookmark-fill' : 'ri-bookmark-line'}`}></i>
                    {fav ? '已收藏' : '收藏追更'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 章节列表 + 相关推荐 */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* 章节 */}
            <div className="lg:col-span-2" id="chapters">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">章节列表</h2>
              <p className="mt-1 text-xs text-foreground-500">共 {manga.chapters} 话 · 更新时间：{manga.updateTime}</p>
              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {latestChapters.map((ch) => (
                  <Link
                    key={ch.no}
                    to={`/manga/${manga.id}/read`}
                    className="group flex items-center justify-between rounded-lg bg-background-100 px-4 py-3 transition hover:bg-primary-100"
                  >
                    <span className="flex items-center gap-2 text-sm text-foreground-700 group-hover:text-primary-700">
                      <i className="ri-book-2-line text-primary-400"></i>
                      {ch.title}
                    </span>
                    <span className="text-xs text-foreground-400">{ch.date}</span>
                  </Link>
                ))}
              </div>
              {manga.chapters > 12 && (
                <p className="mt-4 text-center text-sm text-foreground-500">仅展示最近 {latestChapters.length} 话，登录后可查看完整目录</p>
              )}
            </div>

            {/* 相关推荐 */}
            <aside aria-label="相关推荐">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
              <div className="mt-5 space-y-4">
                {related.map((m) => (
                  <div key={m.id} className="flex gap-3 rounded-lg bg-background-100 p-3 transition hover:bg-primary-100">
                    <Link to={`/manga/${m.id}`} className="shrink-0">
                      <img src={m.cover} alt={`${m.title} 封面`} className="h-24 w-16 rounded-md object-cover object-top" />
                    </Link>
                    <div className="flex flex-col justify-center">
                      <Link to={`/manga/${m.id}`} className="text-sm font-semibold text-foreground-900 transition hover:text-primary-600">
                        {m.title}
                      </Link>
                      <span className="mt-1 text-xs text-foreground-500">{m.categoryName} · {m.status}</span>
                      <span className="mt-1 flex items-center gap-1 text-xs text-accent-600">
                        <i className="ri-star-fill"></i>{m.rating} · {m.views}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </aside>
          </div>
        </section>

        {/* 相关作品卡片 */}
        {related.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">同类型推荐</h2>
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5">
              {related.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      {/* Book 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Book',
          name: manga.title,
          author: { '@type': 'Person', name: manga.author },
          inLanguage: 'zh-CN',
          datePublished: manga.year,
          aggregateRating: { '@type': 'AggregateRating', ratingValue: manga.rating, bestRating: 10, worstRating: 1 },
          genre: manga.tags,
          numberOfPages: manga.chapters,
        })}
      </script>
    </>
  );
}